import {type PIXI} from '@colonist/pixi'
import {type UIGameManagerMethods} from '../ui_game/UIGameManager.ts'
import {createDivIn, createExternalImgIn, createParagraphIn} from '../ui_utils/DomUtils.ts'
import {type EndGameState} from '@colonist/game-states'
import IconLogo from '@static/images/logo.svg'
import IconArrowWhite from '@static/images/icon_arrow_white.svg'

export abstract class UIBaseShareImage {
    protected readonly uiGameManager: UIGameManagerMethods
    protected readonly endGameState: EndGameState

    protected readonly extract: PIXI.IExtract

    protected readonly container: HTMLDivElement

    private readonly imageLoadingCallback?: ((isLoading: boolean) => void) | undefined
    private imageGenerated: boolean = false
    private imageFile: File | undefined
    private convertImagePromise: Promise<File | undefined> | undefined

    constructor(uiGameManager: UIGameManagerMethods, endGameState: EndGameState, imageLoadingCallback?: (isLoading: boolean) => void) {
        this.uiGameManager = uiGameManager
        this.endGameState = endGameState
        this.imageLoadingCallback = imageLoadingCallback
        this.extract = this.uiGameManager.canvasController.app.renderer.extract
        this.extract = this.uiGameManager.canvasController.app.renderer.extract

        this.container = createDivIn(document.body, 'share-image-container')

        this.createLogo()
        this.createColonistLink()
    }

    async getImageFile(): Promise<File | undefined> {
        if(this.imageFile) {
            this.imageLoadingCallback?.(false)
            return this.imageFile
        }
        this.imageLoadingCallback?.(true)

        if(!this.imageGenerated) {
            await this.generateImage()
            this.imageGenerated = true
        }

        if(!this.convertImagePromise) this.convertImagePromise = this.convertImage()

        await this.convertImagePromise
        this.imageLoadingCallback?.(false)
        return this.imageFile
    }

    protected abstract generateImage(): void

    private async convertImage(): Promise<File | undefined> {
        const html2canvasModule: any = await import('html2canvas-pro')
        const html2canvas: (element: HTMLElement, options?: any) => Promise<HTMLCanvasElement> = html2canvasModule.default
        const canvas = await html2canvas(this.container, {useCORS: true})

        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if(!blob) {
                    resolve(undefined)
                    return
                }
                this.imageFile = new File([blob], 'colonist-game-results.png', {type: blob.type})
                resolve(this.imageFile)
            }, 'image/png')
        })
    }

    private createLogo() {
        createExternalImgIn(this.container, IconLogo, {key: 'strings:colonistCommon.utils.emptyString'}, 'share-image-logo')
    }

    private createColonistLink() {
        const linkContainer = createDivIn(this.container, 'share-image-url-container')
        createExternalImgIn(linkContainer, IconArrowWhite, {key: 'strings:colonistCommon.utils.emptyString'}, 'share-image-url-arrow')
        createParagraphIn(linkContainer, {key: 'strings:colonistCommon.utils.#buffer', options: {value: 'colonist.io'}}, 'share-image-url')
    }
}
